import ChatIcon from "@mui/icons-material/Chat";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppContainer from "../../../components/containers/AppContainer";
import CustomBar from "../../../components/customBar";
import NavigationDrawer from "../../../components/navigationDrawer";
import { setSelectedChat } from "../../../redux/slices/selectedChatSlice";

const JobSeekerProfile = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const jobSeeker = state?.jobSeekerId || {};
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(""); // "resume" | "document"
  const [dialogUrl, setDialogUrl] = useState("");

  const handleNavigateToChat = () => {
    let params = state?.jobSeekerId;
    localStorage.setItem("selectedUser", JSON.stringify(params));
    dispatch(setSelectedChat(params));
    navigate("/chat-booking", { state: state });
  };

  const handleOpenDialog = (type, url) => {
    setDialogType(type);
    setDialogUrl(url);
    setDialogOpen(true);
  };

  return (
    <NavigationDrawer>
      <CustomBar title="JOB SEEKER PROFILE" />
      <AppContainer>
        <CardContent>
          {/* Profile Section */}
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar
              sx={{
                width: 90,
                height: 90,
                mb: 2,
                bgcolor: "#eee",
                color: "#6a11cb",
                fontSize: 32,
              }}
            >
              {jobSeeker?.firstname?.charAt(0) || "U"}
            </Avatar>
            <Typography variant="h5" fontWeight="bold">
              {jobSeeker?.firstname || "Unknown"} {jobSeeker?.lastname || ""}
            </Typography>
            <Chip
              label={state?.employmentStatus || "Active"}
              color="success"
              sx={{ mt: 1 }}
            />
            <Stack
              direction="row"
              spacing={2}
              mt={3}
              justifyContent="center"
              width={300}
            >
              <Button
                variant="contained"
                startIcon={<ChatIcon />}
                sx={{ bgcolor: "#ff1744", color: "#fff" }}
                onClick={handleNavigateToChat}
              >
                Chat
              </Button>
            </Stack>
          </Box>

          {/* Personal Info */}
          <Box mt={3} p={2} borderRadius={2} bgcolor="rgba(255, 255, 255, 0.2)">
            <Typography variant="body2">
              <strong>Email:</strong> {jobSeeker.email || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Education:</strong>{" "}
              {state?.education?.[0]?.degreeName || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Experience:</strong>{" "}
              {state?.experience?.[0]?.companyName || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Position:</strong>{" "}
              {state?.experience?.[0]?.jobTitle || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>About:</strong> {state?.about || "N/A"}
            </Typography>
          </Box>

          {/* Basic Info */}
          <Box mt={3} p={2} borderRadius={2} bgcolor="rgba(255, 255, 255, 0.2)">
            <Typography variant="subtitle1" fontWeight="bold">
              Basic Info
            </Typography>
            <Typography variant="body2">
              <strong>Date Of Birth:</strong>{" "}
              {state?.dob ? moment(state.dob).format("MM/DD/YYYY") : "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Gender:</strong> {state?.gender || "N/A"}
            </Typography>
            {/* <Typography variant="body2">
              <strong>National Services:</strong>{" "}
              {state?.nationalServices || "N/A"}
            </Typography> */}
            <Typography variant="body2">
              <strong>Employment Status:</strong>{" "}
              {state?.employmentStatus || "N/A"}
            </Typography>
          </Box>

          {/* Education Section */}
          {state?.education?.length > 0 && (
            <Box
              mt={3}
              p={2}
              borderRadius={2}
              bgcolor="rgba(255, 255, 255, 0.2)"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Education
              </Typography>
              {state.education.map((edu) => (
                <Card
                  key={edu._id}
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <Typography variant="h6">{edu.degreeName}</Typography>
                  <Typography variant="body2">
                    <strong>Institution:</strong> {edu.school}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong>{" "}
                    {moment(edu.startDate).format("YYYY")} -{" "}
                    {edu.isCurrent === "true"
                      ? "Present"
                      : moment(edu.endDate).format("YYYY")}
                  </Typography>

                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleOpenDialog("document", edu.document)}
                    >
                      View Document
                    </Button>
                  </Box>
                </Card>
              ))}
            </Box>
          )}

          {/* Experience Section */}
          {state?.experience?.length > 0 && (
            <Box
              mt={3}
              p={2}
              borderRadius={2}
              bgcolor="rgba(255, 255, 255, 0.2)"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Experience
              </Typography>
              {state.experience.map((exp) => (
                <Card
                  key={exp._id}
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <Typography variant="h6">{exp.jobTitle}</Typography>
                  <Typography variant="body2">
                    <strong>Company:</strong> {exp.companyName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Responsibilities:</strong> {exp.responsibilities}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong>{" "}
                    {moment(exp.startDate).format("YYYY")} -{" "}
                    {exp.isCurrent === "true"
                      ? "Present"
                      : moment(exp.endDate).format("YYYY")}
                  </Typography>
                </Card>
              ))}
            </Box>
          )}

          {/* Resume Section */}
          {state?.resumeUrl && (
            <Box
              mt={3}
              p={2}
              borderRadius={2}
              bgcolor="rgba(255, 255, 255, 0.2)"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Resume
              </Typography>
              <Box mt={1}>
                <Button
                  variant="contained"
                  onClick={() => handleOpenDialog("resume", state.resumeUrl)}
                >
                  View Resume
                </Button>
              </Box>
            </Box>
          )}

          {/* Dynamic Dialog */}
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogContent>
              {dialogUrl ? (
                <img
                  src={dialogUrl}
                  alt={dialogType === "resume" ? "Resume" : "Document"}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              ) : (
                <Typography>No file available</Typography>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </AppContainer>
    </NavigationDrawer>
  );
};

export default JobSeekerProfile;
